import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./generated/schema";

const baseUrl = import.meta.env.VITE_API_URL ?? "";

export class ForbiddenError extends Error {
  constructor(message?: string) {
    // The default covers role-level refusals; resource-scope 403s (e.g. an
    // HOD acting outside their own department) carry a meaningful backend
    // detail worth surfacing in toasts, so callers may pass it through.
    super(message ?? "You don't have permission to access this resource.");
    this.name = "ForbiddenError";
  }
}

/** 409 — the server refused because of current state (already running,
 * duplicate, no new data). Callers branch on it for non-error UX. */
export class ConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConflictError";
  }
}

let configPromise: Promise<{ loginUrl: string; logoutUrl: string }> | null = null;

export function getAuthConfig() {
  if (!configPromise) {
    configPromise = fetch(`${baseUrl}/api/auth/config`)
      .then((r) => r.json())
      .then((data: { login_url: string; logout_url: string }) => ({
        loginUrl: data.login_url,
        logoutUrl: data.logout_url,
      }));
  }
  return configPromise;
}

const authMiddleware: Middleware = {
  async onResponse({ response, request }) {
    if (response.status === 401 && !request.url.endsWith("/auth/me")) {
      const { loginUrl } = await getAuthConfig();
      const redirect = encodeURIComponent(window.location.href);
      window.location.href = `${loginUrl}&redirect=${redirect}`;
    }
    return undefined;
  },
};

export const api = createClient<paths>({ baseUrl });
api.use(authMiddleware);

/** Throw typed errors from an openapi-fetch result, surfacing the backend's
 *  detail + violations[] when present so toasts explain what actually went
 *  wrong (e.g. "spread weight 0.75% exceeds 0.1%") instead of a generic
 *  "Request failed". */
export function throwIfError<T>(
  result: { data?: T; error?: unknown; response: Response },
  fallbackMessage = "Request failed",
): T {
  if (result.response.status === 403)
    throw new ForbiddenError(extractErrorMessage(result.error, "You don't have permission to access this resource."));
  if (result.response.status === 409)
    throw new ConflictError(extractErrorMessage(result.error, fallbackMessage));
  if (result.error) throw new Error(extractErrorMessage(result.error, fallbackMessage));
  return result.data as T;
}

interface BackendErrorShape {
  detail?: string | unknown;
  message?: string;
  violations?: { code?: string; message?: string }[];
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === "string") return error;
  if (!error || typeof error !== "object") return fallback;
  const body = error as BackendErrorShape;
  const headline =
    typeof body.detail === "string"
      ? body.detail
      : typeof body.message === "string"
      ? body.message
      : null;
  const violations = Array.isArray(body.violations) ? body.violations : [];
  if (headline && violations.length > 0) {
    const detail = violations
      .map((v) => v?.message ?? v?.code ?? "")
      .filter(Boolean)
      .join("; ");
    return `${headline} — ${detail}`;
  }
  return headline ?? fallback;
}

export type { paths, components } from "./generated/schema";
